import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Select,
    MenuItem,
    Paper,
    Typography,
    Avatar,
    Link,
    InputAdornment,
    Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LanguageIcon from '@mui/icons-material/Language';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import TagIcon from '@mui/icons-material/Tag';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CircularProgress from '@mui/material/CircularProgress';

const APP_URL = process.env.REACT_APP_APP_URL;

const getUserTimezone = () => {
    // Get user's timezone from browser
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const convertToUTC = (localDate, localTime) => {
    const [hours] = localTime.split(':');
    const date = new Date(localDate);
    date.setHours(parseInt(hours), 0, 0, 0);
    return date.toISOString();
};

const convertFromUTC = (utcDate) => {
    const localDate = new Date(utcDate);
    return {
        date: localDate,
        time: `${String(localDate.getHours()).padStart(2, '0')}:00`
    };
};

const formatLocalDateTime = (date, time) => {
    if (!date || !time) return '';
    
    // Format date in user's locale
    const formattedDate = date.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });

    // Format time in 24-hour format
    const formattedTime = time;

    return `${formattedDate} ${formattedTime}`;
};

// Add a new PostMenu component
const PostMenu = ({ post, onEdit, onDelete, onStatusChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleStatusChange = (newStatus) => {
        onStatusChange(post.id, newStatus);
        handleClose();
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleClick}
                sx={{
                    position: 'absolute',
                    right: 4,
                    top: 4,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                }}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
            >
                <MenuItem onClick={() => {
                    onEdit(post);
                    handleClose();
                }}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} />
                    Edit
                </MenuItem>
                {/* <MenuItem onClick={() => handleStatusChange('waiting_for_schedule')}>
                    <AutoFixHighIcon fontSize="small" sx={{ mr: 1 }} />
                    Set to Schedule
                </MenuItem>
                <MenuItem onClick={() => handleStatusChange('waiting_for_posting')}>
                    <AutoFixHighIcon fontSize="small" sx={{ mr: 1 }} />
                    Set to Posting
                </MenuItem> */}
                <MenuItem
                    onClick={() => {
                        onDelete(post.id);
                        handleClose();
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>
        </>
    );
};

const HeaderButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    margin: '0 4px',
    borderRadius: '20px',
}));

const TimeCell = styled(Box)(({ theme }) => ({
    padding: '30px 10px',
    borderRight: '1px solid #e0e0e0',
    width: '60px',
    color: '#666',
}));

const DayCell = styled(Box)(({ theme, disabled }) => ({
    flex: 1,
    minHeight: '80px',
    borderRight: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
    padding: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: disabled ? '#f5f5f5' : 'inherit',
    position: 'relative', // Add this
    '&:hover': {
        backgroundColor: disabled ? '#f5f5f5' : '#f0f7ff',
    },
}));

const ScheduledPost = styled(Box)(({ theme, status, isGenerating }) => ({
    position: 'absolute',
    left: '4px',
    right: '4px',
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: isGenerating 
        ? '#f5f5f5' 
        : status === 'waiting_for_schedule' 
            ? '#e3f2fd' 
            : status === 'posted' // New status check
                ? '#dfd1e7' // Color for posted status
                : '#e8f5e9',
    border: `1px solid ${status === 'waiting_for_schedule' ? '#90caf9' : status === 'posted' ? '#9566bb' : '#66bb6a'}`,
    cursor: isGenerating ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    opacity: isGenerating ? 0.7 : 1,
    '&:hover': {
        boxShadow: isGenerating ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
    }
}));

const CreatePostDialog = ({ open, onClose, selectedSlot, onSchedule, initialPost, firebase }) => {
    const [content, setContent] = useState('');
    const [tags, setTags] = useState([]);
    const [mediaFile, setMediaFile] = useState(null);
    const [showTagSearch, setShowTagSearch] = useState(false);
    const [aspectRatio, setAspectRatio] = useState("5:4");
    const [isCheckedRaw, setIsCheckedRaw] = useState(true);

    useEffect(() => {
        if (open && initialPost) {
            // Update content based on post status
            // if (initialPost.status === 'waiting_for_posting') {
            //     setContent(initialPost.title || initialPost.content);
            // } else {
            //     setContent(initialPost.content);
            // }
            setContent(initialPost.title || initialPost.content);
            setTags(initialPost.tags);
            setMediaFile(initialPost.mediaFile);
        } else if (!open) {
            setContent('');
            setTags([]);
            setMediaFile(null);
        }
    }, [open, initialPost]);

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
            setMediaFile(file);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setMediaFile(file);
        }
    };

    const handleGenerateContent = () => {
        // Implement AI generation logic here
        console.log('Generating content...');
    };

    const handleSchedulePost = async () => {
        // For creating new posts
        if (!mediaFile) {
            alert('Please add an image before scheduling the post');
            return;
        }

        try {
            const mediaUrl = await firebase.uploadAdminFile(mediaFile);

            const postData = {
                id: Date.now(),
                content: content,
                tags,
                mediaFile,
                mediaUrl: mediaUrl,
                scheduledTime: selectedSlot,
                status: 'waiting_for_schedule',
                aspectRatio: aspectRatio,
                raw: isCheckedRaw,
                mediaPreview: mediaFile ? URL.createObjectURL(mediaFile) : null,
            };

            onSchedule(postData, false);
            onClose();
        } catch (error) {
            console.error('Error creating post:', error);
            alert('Failed to create post. Please try again.');
        }
    };

    const handleEditPost = async () => {
        // For editing existing posts
        try {
            const updatedData = {
                title: content, // Update the title
                updatedAt: new Date(),
            };

            await firebase.updateScheduledPost(initialPost.id, updatedData);

            const postData = {
                ...initialPost,
                title: content,
                updatedAt: new Date(),
            };

            onSchedule(postData, true);
            onClose();
        } catch (error) {
            console.error('Error updating post:', error);
            alert('Failed to update post. Please try again.');
        }
    };

    const clickOnSetAspectRatio = (value) => {
        setAspectRatio(value);
    }

    const handleCheckboxChange = (event) => {
        setIsCheckedRaw(event.target.checked);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    {initialPost?.status === 'waiting_for_posting' ? 'Edit Post' : 'Create Post'}
                </Typography>
                {!initialPost?.status === 'waiting_for_posting' && (
                    <Button
                        variant="outlined"
                        onClick={() => setShowTagSearch(!showTagSearch)}
                        endIcon={<KeyboardArrowDownIcon />}
                    >
                        Add Tags
                    </Button>
                )}
            </DialogTitle>

            {showTagSearch && !initialPost?.status === 'waiting_for_posting' && (
                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <TextField
                        fullWidth
                        placeholder="Search or create tag"
                        variant="outlined"
                        sx={{ bgcolor: 'white' }}
                    />
                </Box>
            )}

            <DialogContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Avatar>
                        <ImageIcon />
                    </Avatar>
                    <TextField
                        fullWidth
                        multiline
                        placeholder={initialPost?.status === 'waiting_for_posting'
                            ? "Edit your post title"
                            : "Start writing or use the AI Assistant"
                        }
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        variant="standard"
                        InputProps={{
                            endAdornment: !initialPost?.status === 'waiting_for_posting' && (
                                <InputAdornment position="end">
                                    <Button
                                        startIcon={<SmartToyIcon />}
                                        variant="contained"
                                        onClick={handleGenerateContent}
                                        sx={{ borderRadius: 5 }}
                                    >
                                        Use the AI Assistant
                                    </Button>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        border: '2px dashed grey.300',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: initialPost?.status === 'waiting_for_posting' ? 'default' : 'pointer',
                        '&:hover': { bgcolor: initialPost?.status === 'waiting_for_posting' ? 'inherit' : 'grey.50' }
                    }}
                    onDrop={initialPost?.status === 'waiting_for_posting' ? null : handleDrop}
                    onDragOver={initialPost?.status === 'waiting_for_posting' ? null : (e) => e.preventDefault()}
                    onClick={initialPost?.status === 'waiting_for_posting' ? null : () => document.getElementById('file-input').click()}
                >
                    <input
                        id="file-input"
                        type="file"
                        hidden
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                        disabled={initialPost?.status === 'waiting_for_posting'}
                    />
                    {(mediaFile || initialPost?.mediaPreview) ? (
                        <Box sx={{ width: '100%', maxHeight: '400px', overflow: 'hidden' }}>
                            {(!mediaFile && initialPost?.mediaPreview) ? (
                                <img
                                    src={initialPost.mediaPreview}
                                    alt="Preview"
                                    style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain' }}
                                />
                            ) : mediaFile?.type.startsWith('image/') ? (
                                <img
                                    src={URL.createObjectURL(mediaFile)}
                                    alt="Preview"
                                    style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain' }}
                                />
                            ) : (
                                <video
                                    src={URL.createObjectURL(mediaFile)}
                                    controls
                                    style={{ width: '100%', height: 'auto', maxHeight: '400px' }}
                                />
                            )}
                        </Box>
                    ) : (
                        <>
                            <ImageIcon sx={{ fontSize: 40, color: 'grey.500', mb: 1 }} />
                            <Typography>
                                Drag & drop or <Link component="span">select a file</Link>
                            </Typography>
                        </>
                    )}
                </Box>

                {/* {!initialPost?.status === 'waiting_for_posting' && ( */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, p: 1, borderTop: 1, borderBottom: 1, borderColor: 'grey.200' }}>
                        <Select
                            size="small"
                            value={aspectRatio}
                            onChange={(e) => 
                                clickOnSetAspectRatio(e.target.value)
                            }
                            sx={{ mr: 1, minWidth: 100 }}
                        >
                            <MenuItem value="1:1">1:1</MenuItem>
                            <MenuItem value="4:5">4:5</MenuItem>
                            <MenuItem value="16:9">16:9</MenuItem>
                            <MenuItem value="2:3">2:3</MenuItem>
                            <MenuItem value="5:4">5:4</MenuItem>
                        </Select>
                        <Checkbox 
                            size="small"
                            checked={isCheckedRaw}
                            onChange={handleCheckboxChange}
                            sx={{ mr: 1 }}
                        />                
                        <IconButton size="small">
                            <ImageIcon />
                        </IconButton>
                        <IconButton size="small">
                            <EmojiEmotionsIcon />
                        </IconButton>
                        <IconButton size="small">
                            <TagIcon />
                        </IconButton>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            startIcon={<SmartToyIcon />}
                            variant="text"
                        >
                            AI Assistant
                        </Button>
                        <Typography variant="body2" color="grey.500" sx={{ ml: 2 }}>
                            {content?.length}/2200
                        </Typography>
                    </Box>
                {/* )} */}

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                        Schedule date: {formatLocalDateTime(selectedSlot?.date, selectedSlot?.time)}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        startIcon={<AutoFixHighIcon />}
                        onClick={initialPost?.status === 'waiting_for_posting' ? handleEditPost : handleSchedulePost}
                    >
                        {initialPost?.status === 'waiting_for_posting' ? 'Save Changes' : 'Add Post'}
                    </Button>
                    {!initialPost?.status === 'waiting_for_posting' && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AutoFixHighIcon />}
                        >
                            Generate Post
                        </Button>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

const Schedules = ({ firebase }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [open, setOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [viewMode, setViewMode] = useState('week');
    const [scheduledPosts, setScheduledPosts] = useState([]);
    const [selectedPost, setSelectedPost] = useState(null);
    const [generatingPosts, setGeneratingPosts] = useState(new Set());
    const [isGeneratingPosts, setIsGeneratingPosts] = useState(false);

    useEffect(() => {
        const fetchSchedules = async () => {
            try {
                const snapshot = await firebase.getScheduledPosts();
    
                const fetchedSchedules = snapshot.docs.map(doc => {
                    const data = doc.data();
                    const utcTimestamp = data.scheduledTime.toDate();
                    
                    // Convert UTC to local time
                    const localTime = convertFromUTC(utcTimestamp);
    
                    return {
                        id: doc.id,
                        content: data.title,
                        mediaPreview: data.mediaUrl,
                        mediaUrl: data.mediaUrl,
                        scheduledTime: localTime,
                        status: data.status,
                        tags: data.tags
                    };
                });
    
                setScheduledPosts(fetchedSchedules);
            } catch (error) {
                console.error('Error fetching schedules:', error);
            }
        };
    
        fetchSchedules();
    }, [firebase]);

    // Generate time slots from 12 AM to 11 PM
    const timeSlots = Array.from({ length: 24 }, (_, i) => {
        return `${String(i).padStart(2, '0')}:00`;
    });

    // Generate days for the current week
    const getDaysInWeek = () => {
        const days = [];
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - (currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1)); // Adjust to start on Monday

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            days.push(date);
        }
        return days;
    };

    const handleStatusChange = (postId, newStatus) => {
        setScheduledPosts(posts =>
            posts.map(post =>
                post.id === postId
                    ? { ...post, status: newStatus }
                    : post
            )
        );
    };

    const handlePrevWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() - 7);
        setCurrentDate(newDate);
    };

    const handleNextWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() + 7);
        setCurrentDate(newDate);
    };

    const isDateTimeExpired = (date, time) => {
        const now = new Date();
        const [hours, period] = time.split(' ');
        let hour = parseInt(hours);

        // Convert to 24-hour format
        if (period === 'PM' && hour !== 12) {
            hour += 12;
        } else if (period === 'AM' && hour === 12) {
            hour = 0;
        }

        const scheduleDate = new Date(date);
        scheduleDate.setHours(hour, 0, 0, 0);

        return scheduleDate < now;
    };

    const handleCellClick = (time, date) => {
        if (!isDateTimeExpired(date, time)) {
            setSelectedSlot({ time, date });
            setOpen(true);
        }
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const handleDeletePost = async (postId) => {
        try {
            const postToDelete = scheduledPosts.find(post => post.id === postId);
            
            if (!postToDelete) {
                console.error('Post not found:', postId);
                return;
            }
    
            console.log('Deleting post:', postToDelete);
            await firebase.deleteScheduledPost(postId);
            
    
            setScheduledPosts(posts => posts.filter(p => p.id !== postId));
            setOpen(false);
            setSelectedPost(null);
        } catch (error) {
            console.error('Error deleting post:', error);
            alert('Failed to delete post. Please try again.');
        }
    };

    const generatePostContent = async (post) => {

        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${firebase.auth.currentUser.accessToken}`,
        };

        const utcTime = convertToUTC(post.scheduledTime.date, post.scheduledTime.time);

        const request = {
            role: "admin",
            quantity: 1,
            image: post.mediaUrl,
            scheduledTime: utcTime,
            title: post.content,
            tags: post.tags.join(','),
            creativity: 3,
            similarity: 0,
            modelId: 'T0cqWhZBvio0SNXp8JGo',
            aspectRatio: post.aspectRatio,
            raw: post.raw,
            isPublic: true,
            timezone: getUserTimezone() // Send user's timezone to server
        };

        try {

            const result = await axios.post(`${APP_URL}/schedule-image`, request, { headers });
            const jobId = result.data.jobId;

            // Poll for job completion
            const pollInterval = 3000; // 2 seconds
            while (true) {
                const jobResult = await axios.get(`${APP_URL}/schedule-image/${jobId}`, { headers });
                const jobData = jobResult.data;

                if (jobData.status === 'waiting_for_posting') {
                    return {
                        id: jobData.jobId,
                        title: jobData.title,
                        imageUrl: jobData.image,
                        mediaPreview: jobData.image,
                        tags: jobData.tags,
                        status: jobData.status
                    };
                } else if (jobData.status === 'failed') {
                    throw new Error('Job failed');
                }

                // Wait before polling again
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
        } catch (error) {
            console.error('Error generating post:', error);
            throw error;
        }
    };

    const handleGeneratePost = async (post) => {
        const postId = post.id;
        setGeneratingPosts(prev => new Set(prev).add(postId));

        try {
            const response = await generatePostContent(post);

            setScheduledPosts(posts => posts.map(post =>
                post.id === postId ? {
                    ...post,
                    content: response.title,
                    mediaPreview: response.imageUrl,
                    mediaFile: null,
                    status: 'waiting_for_posting'
                } : post
            ));
        } catch (error) {
            console.error('Failed to generate post:', error);
        } finally {
            setGeneratingPosts(prev => {
                const newSet = new Set(prev);
                newSet.delete(postId);
                return newSet;
            });
        }
    };

    const handleGenerateAllPosts = async () => {
        setIsGeneratingPosts(true);
        const waitingPosts = scheduledPosts.filter(post => post.status === 'waiting_for_schedule');

        if (waitingPosts.length < 1) {
            alert("Empty posts");
            return;
        }
        // Generate all posts concurrently
        await Promise.all(
            waitingPosts.map(post => handleGeneratePost(post))
        );
        setIsGeneratingPosts(false);
    };

    return (
        <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={handlePrevWeek}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton onClick={handleNextWeek}>
                        <ChevronRightIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ mx: 2 }}>
                        {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </Typography>
                    <HeaderButton variant="outlined" onClick={handleToday}>Today</HeaderButton>
                    {/* <Select
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        <MenuItem value="week">Week</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                    </Select> */}
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AutoFixHighIcon />}
                        onClick={handleGenerateAllPosts}
                        disabled={isGeneratingPosts}
                    >
                        {isGeneratingPosts? 'Generating' : 'Generate All Posts'}
                    </Button>
                </Box>
                <Box>
                    <HeaderButton startIcon={<FilterListIcon />}>
                        All Posts
                    </HeaderButton>
                    <HeaderButton startIcon={<LocalOfferIcon />}>
                        Tags
                    </HeaderButton>
                    <HeaderButton startIcon={<LanguageIcon />}>
                        Saigon
                    </HeaderButton>
                </Box>
            </Box>

            {/* Calendar Grid */}
            <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                {/* Days Header */}
                <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
                    <Box sx={{ width: '60px' }} /> {/* Empty corner */}
                    {getDaysInWeek().map((date) => (
                        <Box
                            key={date.toISOString()}
                            sx={{
                                flex: 1,
                                p: 2,
                                textAlign: 'center',
                                borderRight: '1px solid #e0e0e0',
                            }}
                        >
                            <Typography variant="subtitle2">
                                {date.toLocaleString('default', { weekday: 'long' })}
                            </Typography>
                            <Typography variant="h6">
                                {date.getDate()}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Time Grid */}
                <Box sx={{ display: 'flex', flex: 1 }}>
                    {/* Time Labels */}
                    <Box sx={{ borderRight: '1px solid #e0e0e0' }}>
                        {timeSlots.map((time) => (
                            <TimeCell key={time}>
                                <Typography variant="caption">{time}</Typography>
                            </TimeCell>
                        ))}
                    </Box>

                    {/* Day Columns */}
                    {getDaysInWeek().map((date) => (
                        <Box key={date.toISOString()} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            {timeSlots.map((time) => {
                                const isExpired = isDateTimeExpired(date, time);
                                const postsAtThisTime = scheduledPosts.filter(
                                    post =>
                                        post.scheduledTime.date.getDate() === date.getDate() &&
                                        post.scheduledTime.date.getMonth() === date.getMonth() &&  // Add this line
                                        post.scheduledTime.date.getFullYear() === date.getFullYear() &&  // Add this line
                                        post.scheduledTime.time === time
                                );

                                return (
                                    <DayCell
                                        key={`${date.toISOString()}-${time}`}
                                        onClick={() => !isExpired && handleCellClick(time, date)}
                                        disabled={isExpired}
                                    >
                                        {postsAtThisTime.map(post => (
                                            <ScheduledPost
                                                key={post.id}
                                                status={post.status}
                                                isGenerating={generatingPosts.has(post.id)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!generatingPosts.has(post.id)) {
                                                        setSelectedPost(post);
                                                        handleCellClick(post.scheduledTime.time, post.scheduledTime.date)
                                                        setOpen(true);
                                                    }
                                                }}
                                            >
                                                {generatingPosts.has(post.id) ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CircularProgress size={20} />
                                                        <Typography>Generating...</Typography>
                                                    </Box>
                                                ) : (
                                                    <>
                                                        {post.mediaPreview && (
                                                            <img
                                                                src={post.mediaPreview}
                                                                alt=""
                                                                style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '4px' }}
                                                            />
                                                        )}
                                                        <Typography noWrap sx={{ flex: 1 }}>{post.content}</Typography>
                                                        <PostMenu
                                                            post={post}
                                                            onEdit={(post) => {
                                                                setSelectedPost(post);
                                                                setOpen(true);
                                                            }}
                                                            onDelete={handleDeletePost}
                                                            onStatusChange={handleStatusChange}
                                                        />
                                                    </>
                                                )}
                                            </ScheduledPost>
                                        ))}
                                    </DayCell>
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Paper>

            <CreatePostDialog
                open={open}
                onClose={() => {
                    setOpen(false);
                    setSelectedPost(null);
                }}
                selectedSlot={selectedSlot}

                onSchedule={(newPost) => setScheduledPosts([...scheduledPosts, newPost])}
                initialPost={selectedPost}
                firebase={firebase}
            />
        </Box>
    );
};

export default Schedules;